import { computed, inject, Service, signal } from '@angular/core';
import {
  form,
  maxLength,
  pattern,
  required,
  requiredError,
  validate,
} from '@angular/forms/signals';
import { cpfValido } from '../../../../shared/utils/documento.util';
import { DesafioDoFormulario, DesafioFormularioService } from '../../services/desafio-formulario.service';
import { PerfilCidadaoService } from '../../services/perfil-cidadao.service';
import {
  AberturaManifestacao,
  ErroOuvidoriaPublica,
  FormaResposta,
  ManifestacaoAbertura,
  TipoManifestacaoOuvidoria,
} from '../models/manifestacao-publica.model';

/** Como a pessoa chegou ao formulário — define se os dados pessoais são coletados. */
export type ModoAcessoOuvidoria = '' | 'identificado' | 'anonimo';

/**
 * Um anexo escolhido pelo cidadão. Guarda o `File` inteiro, e não só nome e tamanho: é ele que
 * viaja no `multipart/form-data` da abertura.
 */
export interface ArquivoAnexo {
  id: number;
  nome: string;
  tamanho: number;
  arquivo: File;
}

/**
 * O formulário, campo a campo alinhado ao `AbrirManifestacaoCommand` do back-end. O que saiu em
 * 08/09/2026 e por quê: **órgão** (resolvido pelo host do portal), **serviço** e **protocolo
 * anterior** (não existem no contrato de abertura), **CEP/número/complemento** (o back-end
 * recebe o endereço num campo só).
 */
export interface SolicitacaoOuvidoria {
  nomeCompleto: string;
  cpfCnpj: string;
  email: string;
  telefone: string;

  endereco: string;
  bairro: string;
  cidade: string;
  uf: string;

  assunto: string;
  descricao: string;

  desejaResposta: boolean;
  formaResposta: FormaResposta;
  aceiteComunicacao: boolean;

  /** Preserva a identidade perante o SETOR, não perante a Ouvidoria. Excludente com o anonimato. */
  sigiloIdentidade: boolean;
}

export const LIMITE_DESCRICAO = 500;

export const LIMITE_ASSUNTO = 200;

const MENSAGEM_OBRIGATORIO = 'Campo obrigatório.';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Vêm prontos do cadastro, então não contam como manifestação preenchida pela pessoa. */
const CAMPOS_PESSOAIS: readonly (keyof SolicitacaoOuvidoria)[] = [
  'nomeCompleto',
  'cpfCnpj',
  'email',
  'telefone',
];

const VALOR_INICIAL: SolicitacaoOuvidoria = {
  nomeCompleto: '',
  cpfCnpj: '',
  email: '',
  telefone: '',
  endereco: '',
  bairro: '',
  cidade: '',
  uf: '',
  assunto: '',
  descricao: '',
  desejaResposta: true,
  formaResposta: 'Email',
  aceiteComunicacao: false,
  sigiloIdentidade: false,
};

/**
 * Aceita o documento vazio (é opcional) e, quando informado, exige CPF válido. O CNPJ de 14
 * dígitos passa sem conferência de dígito — é exatamente o que o validator do back-end faz, e
 * divergir aqui criaria recusa que a API não faria.
 */
export function documentoValido(valor: string): boolean {
  const digitos = String(valor ?? '').replace(/\D/g, '');

  if (!digitos) return true;

  return digitos.length === 14 || cpfValido(digitos);
}

/**
 * Guarda a manifestação enquanto a pessoa navega pelas etapas. Vive no componente do fluxo
 * (`providers`), e não na raiz, porque cada etapa é recriada a cada troca de passo e os dados
 * precisam sobreviver às idas e vindas — inclusive para a etapa de revisão.
 *
 * O canal do órgão **não** mora aqui: ele é do `OuvidoriaPublicaService`, na raiz, para o cache
 * sobreviver à saída da rota.
 */
@Service({ autoProvided: false })
export class ManifestacaoStore {
  private readonly perfil = inject(PerfilCidadaoService);

  readonly modoAcesso = signal<ModoAcessoOuvidoria>('');

  /** Login feito pelo card da etapa de informações — sobrevive à troca de etapas. */
  readonly sessaoIniciada = signal(false);

  readonly identificado = computed(() => this.modoAcesso() === 'identificado');

  readonly anonimo = computed(() => this.modoAcesso() === 'anonimo');

  readonly dados = signal<SolicitacaoOuvidoria>({ ...VALOR_INICIAL });

  readonly solicitacaoForm = form(this.dados, (caminho) => {
    const identificada = () => this.identificado();

    // Os dados pessoais são EDITÁVEIS (mudança de 08/09/2026): vêm de um cadastro que ainda é
    // mock e sem CPF/telefone confiáveis, e é por nome e e-mail que a resposta sai.
    required(caminho.nomeCompleto, { message: MENSAGEM_OBRIGATORIO, when: identificada });
    maxLength(caminho.nomeCompleto, 200);

    required(caminho.email, { message: MENSAGEM_OBRIGATORIO, when: identificada });
    pattern(caminho.email, EMAIL, { message: 'Informe um e-mail válido.', when: identificada });
    maxLength(caminho.email, 150);

    // CPF/CNPJ é opcional; informado, precisa ser possível. Em branco não gera pendência.
    validate(caminho.cpfCnpj, ({ value }) =>
      !identificada() || documentoValido(value())
        ? null
        : requiredError({ message: 'Informe um CPF ou CNPJ válido.' }),
    );

    maxLength(caminho.telefone, 20);
    maxLength(caminho.endereco, 300);
    maxLength(caminho.bairro, 100);
    maxLength(caminho.cidade, 100);

    required(caminho.assunto, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.assunto, LIMITE_ASSUNTO, {
      message: `O assunto deve ter no máximo ${LIMITE_ASSUNTO} caracteres.`,
    });

    required(caminho.descricao, { message: MENSAGEM_OBRIGATORIO });
    maxLength(caminho.descricao, LIMITE_DESCRICAO);

    required(caminho.formaResposta, { message: MENSAGEM_OBRIGATORIO });

    // Signal Forms não tem `requiredTrue`; a mensagem é a mesma que o back-end devolveria.
    validate(caminho.aceiteComunicacao, ({ value }) =>
      value()
        ? null
        : requiredError({
            message: 'É necessário aceitar o contato para registrar a manifestação.',
          }),
    );
  });

  readonly arquivos = signal<readonly ArquivoAnexo[]>([]);

  /**
   * O desafio de formulário e o honeypot desta manifestação (change
   * anti-abuso-superficie-publica): pedido na carga da rota, enviado no cabeçalho do POST.
   */
  readonly desafio = new DesafioDoFormulario(inject(DesafioFormularioService));

  /** A resposta do 201, lida pela etapa de Confirmação sem `input()` novo. */
  readonly respostaAbertura = signal<ManifestacaoAbertura | null>(null);

  /** O erro da API em tratamento local — a etapa de destino o exibe. */
  readonly erroEnvio = signal<ErroOuvidoriaPublica | null>(null);

  readonly caracteresDescricao = computed(() => this.dados().descricao.length);

  readonly caracteresAssunto = computed(() => this.dados().assunto.length);

  readonly valido = computed(() => this.solicitacaoForm().valid());

  /** Só há o que cancelar depois que algum campo da manifestação foi preenchido. */
  readonly preenchido = computed(() => {
    const valores = this.dados();

    const alterado = (Object.keys(valores) as (keyof SolicitacaoOuvidoria)[])
      .filter((campo) => !this.identificado() || !CAMPOS_PESSOAIS.includes(campo))
      .some((campo) => {
        const valor = valores[campo];
        const inicial = VALOR_INICIAL[campo];

        return typeof valor === 'boolean' ? valor !== inicial : valor.trim() !== inicial;
      });

    return alterado || this.arquivos().length > 0;
  });

  private proximoId = 1;

  /** Traz os dados pessoais do cadastro sem sobrescrever o que já estiver preenchido. */
  preencherComPerfil(): void {
    const cadastro = this.perfil.cadastro();

    this.dados.update((atual) => ({
      ...atual,
      nomeCompleto: atual.nomeCompleto || cadastro.nomeCompleto,
      cpfCnpj: atual.cpfCnpj || cadastro.cpf,
      email: atual.email || cadastro.email,
      telefone: atual.telefone || cadastro.telefoneCelular01 || cadastro.telefoneResidencial,
    }));
  }

  entrarIdentificado(): void {
    this.modoAcesso.set('identificado');
    this.preencherComPerfil();
  }

  /**
   * Sem identificação. Zera o que já tiver sido preenchido de dado pessoal — quem escolhe o
   * anonimato depois de fazer login não espera que o nome viaje assim mesmo — e desliga o
   * sigilo, que a API rejeita junto com o anonimato.
   */
  entrarAnonimo(): void {
    this.modoAcesso.set('anonimo');

    this.dados.update((atual) => ({
      ...atual,
      nomeCompleto: '',
      cpfCnpj: '',
      email: '',
      telefone: '',
      sigiloIdentidade: false,
    }));
  }

  adicionarArquivos(lista: FileList | null | undefined): void {
    if (!lista?.length) return;

    const novos = Array.from(lista).map((arquivo) => ({
      id: this.proximoId++,
      nome: arquivo.name,
      tamanho: arquivo.size,
      arquivo,
    }));

    this.arquivos.update((atuais) => [...atuais, ...novos]);
  }

  removerArquivo(id: number): void {
    this.arquivos.update((lista) => lista.filter((arquivo) => arquivo.id !== id));
  }

  /** Os `File` originais, na ordem em que foram adicionados — é o que vai no `FormData`. */
  arquivosParaEnvio(): readonly File[] {
    return this.arquivos().map((anexo) => anexo.arquivo);
  }

  /**
   * O payload da abertura. **Não leva órgão** (o back-end o resolve pelo host) e, na anônima,
   * não leva dado pessoal algum — nem que o cidadão os tenha preenchido antes de trocar de
   * modo — e força `sigiloIdentidade = false`, que a API recusa junto com o anonimato.
   */
  paraPayload(tipo: TipoManifestacaoOuvidoria): AberturaManifestacao {
    const dados = this.dados();
    const anonima = this.anonimo();

    const base: AberturaManifestacao = {
      desTipoManifestacao: tipo,
      desAssunto: dados.assunto.trim(),
      desDescricao: dados.descricao.trim(),
      bitAnonima: anonima,
      bitSigiloIdentidade: anonima ? false : dados.sigiloIdentidade,
      bitDesejaResposta: dados.desejaResposta,
      bitAceiteComunicacao: dados.aceiteComunicacao,
      desFormaResposta: dados.formaResposta,
      desEndereco: dados.endereco.trim(),
      desBairro: dados.bairro.trim(),
      desCidade: dados.cidade.trim(),
      desUf: dados.uf.trim(),
    };

    if (anonima) return base;

    return {
      ...base,
      desNome: dados.nomeCompleto.trim(),
      desCpfCnpj: dados.cpfCnpj.trim(),
      desEmail: dados.email.trim(),
      desTelefone: dados.telefone.trim(),
    };
  }

  limpar(): void {
    this.modoAcesso.set('');
    this.sessaoIniciada.set(false);
    this.dados.set({ ...VALOR_INICIAL });
    this.arquivos.set([]);
    this.respostaAbertura.set(null);
    this.erroEnvio.set(null);

    // Cancelar e recomeçar não reaproveitam o desafio: a próxima manifestação pede o seu.
    this.desafio.descartar();
    this.desafio.carregar();
  }
}
